import type { DataTableCell } from '@shared/types/views';
import type { AppCollection, AppMember, AppRecord, AppStatus } from '@/lib/app-records';
import type { AggregatableObject } from '@shared/app-aggregates';
import { type CellFormat, columnFormat } from '@shared/cell-format';

/** What the list shows: search, one status, one label, my objects, done hidden or not, archived or not. */
export interface AppListQuery {
  search: string;
  status: string | null;
  label: string | null;
  mine: boolean;
  hideDone: boolean;
  archived: boolean;
  /** Only the objects with a next action (N2), e.g. opened from Aujourd'hui. */
  withNextAction: boolean;
}

export const EMPTY_APP_LIST_QUERY: AppListQuery = { search: '', status: null, label: null, mine: false, hideDone: false, archived: false, withNextAction: false };

const normalize = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const cellText = (cell: DataTableCell | undefined): string => {
  const value = cell?.value;
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  return String(value);
};

/** U2: the format of each column, read once over the objects of the list. */
export const columnFormats = (collection: AppCollection, records: AppRecord[]): Record<string, CellFormat> =>
  Object.fromEntries(collection.columns.map((column) => [column.key,
    columnFormat(column, records.map((record) => record.computed.find((cell) => cell.key === column.key)))]));

/** Columns a list shows besides the title when the app's description does not choose them. */
export const DEFAULT_LIST_COLUMNS = 3;

/**
 * The columns the list shows after the title, in order: those the app's
 * description chooses, otherwise the first ones that are not paragraphs (U2). They are the columns that
 * decide; an object's drawer shows them first and the rest below.
 */
export const listColumns = (collection: AppCollection, visibleColumns?: string[], formats?: Record<string, CellFormat>) => {
  const rest = collection.columns.slice(1);
  return visibleColumns
    ? visibleColumns.flatMap((key) => rest.filter((column) => column.key === key))
    : rest.filter((column) => !formats || formats[column.key] !== 'long_text').slice(0, DEFAULT_LIST_COLUMNS);
};

export const recordTitle = (record: AppRecord, collection: AppCollection): string =>
  cellText(record.computed.find((cell) => cell.key === collection.columns[0]?.key)) || record.recordKey;

export const statusOf = (collection: AppCollection, key: string): AppStatus =>
  collection.statuses.find((status) => status.key === key) ?? { key, label: key, tone: 'neutral' };

export const isDone = (collection: AppCollection, record: AppRecord) => statusOf(collection, record.status).done === true;

export const filterAppRecords = (
  records: AppRecord[], collection: AppCollection, query: AppListQuery, userId: string | null, labelsByRecord: Record<string, string[]> = {},
  /** Objects with a next action; null while not read (the filter then waits). */
  nextActionIds: ReadonlySet<string> | null = null,
): AppRecord[] => {
  const needle = normalize(query.search.trim());
  return records.filter((record) =>
    (query.archived ? record.archivedAt !== null : record.archivedAt === null)
    && (!query.status || record.status === query.status)
    && (!query.label || (labelsByRecord[record.id] ?? []).includes(query.label))
    && (!query.mine || (userId !== null && record.assigneeUserId === userId))
    && (!query.hideDone || !isDone(collection, record))
    && (!query.withNextAction || !nextActionIds || nextActionIds.has(record.id))
    && (!needle || record.computed.some((cell) => normalize(cellText(cell)).includes(needle))));
};

/** Count per status among the records the archive switch shows. */
export const statusCounts = (records: AppRecord[], archived: boolean): Record<string, number> =>
  records.filter((record) => (record.archivedAt !== null) === archived)
    .reduce<Record<string, number>>((counts, record) => ({ ...counts, [record.status]: (counts[record.status] ?? 0) + 1 }), {});

/** « aujourd'hui », « hier », « 3 oct. » */
export const seenLabel = (iso: string, now: Date = new Date()): string => {
  const date = new Date(iso);
  const day = (value: Date) => new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
  const diff = Math.round((day(now) - day(date)) / 86_400_000);
  if (diff === 0) return 'aujourd’hui';
  if (diff === 1) return 'hier';
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }) });
};

/** F6b: an object as the app's figures and charts read it (people by name; archived objects are left out by the caller). */
export const toAggregatable = (record: AppRecord, members: AppMember[]): AggregatableObject & { id: string } => {
  const nameOf = (userId: string | null) => members.find((member) => member.userId === userId)?.name ?? null;
  return { id: record.id, status: record.status, assignee: nameOf(record.assigneeUserId), owner: nameOf(record.ownerUserId), cells: record.computed };
};
